import type { ReportDataTableColumn } from '../../components/ReportDataTable'
import { formatAmount } from '@/lib/utils/formatters'
import { LegacyHtmlPreview } from '../../components/LegacyHtmlPreview'
import type { ConfiguredReportReportRow } from '../../types/pages'
import { display, makeLink, numberValue, reimbursementStatusLabel } from '../../utils/configuredReportFormatters'
import { PENDING_DEFAULTS_HEADER_CLASS } from './shared'

export const pendingDefaultsColumns: ReportDataTableColumn<ConfiguredReportReportRow, string>[] = [
  {
    id: 'applicant_id',
    label: 'Resident',
    sortKey: 'applicant_id',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[7rem]`,
    className: 'min-w-[7rem]',
    render: (row) =>
      row.applicant_id && row.fk_user_id
        ? makeLink(
            `/defaults/default-detail/${row.applicant_id}/${row.fk_user_id}`,
            display(row.applicant_id),
            true,
          )
        : display(row.applicant_id),
  },
  {
    id: 'resident_name',
    label: 'Resident Name',
    sortKey: 'resident_name',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem]',
    render: (row) => display(row.resident_name),
  },
  {
    id: 'property_name',
    label: 'Property Name',
    sortKey: 'property_name',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[12rem]`,
    className: 'min-w-[12rem]',
    render: (row) => display(row.property_name),
  },
  {
    id: 'company_name',
    label: 'Company Name',
    sortKey: 'company_name',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[12rem]`,
    className: 'min-w-[12rem]',
    render: (row) =>
      row.company_id
        ? makeLink(
            `/company/edit/${row.company_id}`,
            display(row.company_name),
            true,
          )
        : display(row.company_name),
  },
  {
    id: 'sales_team',
    label: 'Sales Team',
    sortKey: 'sales_team',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[8rem]`,
    className: 'min-w-[8rem]',
    render: (row) => display(row.sales_team),
  },
  {
    id: 'certificate_amount',
    label: 'Amount Paid For Contract',
    sortKey: 'certificate_amount',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem] whitespace-nowrap text-right',
    render: (row) =>
      formatAmount(
        numberValue(row.certificate_amount) + numberValue(row.surcharge_fee),
      ),
  },
  {
    id: 'contract_start_date',
    label: 'Lease Start Date',
    sortKey: 'contract_start_date',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem] whitespace-nowrap',
    render: (row) => display(row.contract_start_date),
  },
  {
    id: 'rental_status',
    label: 'Rental Status',
    sortKey: 'rental_status',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[8rem]`,
    className: 'min-w-[8rem]',
    render: (row) => display(row.rental_status),
  },
  {
    id: 'rental_progress',
    label: 'Rental Progress',
    sortKey: 'rental_progress',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[9rem]`,
    className: 'min-w-[9rem]',
    render: (row) => display(row.rental_progress),
  },
  {
    id: 'claim_status',
    label: 'Reimbursement Status',
    sortKey: 'claim_status',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem]',
    render: (row) => reimbursementStatusLabel(row.claim_status),
  },
  {
    id: 'net_claim_amount',
    label: 'Net Reimbursement Amount',
    sortKey: 'net_claim_amount',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[11rem]`,
    className: 'min-w-[11rem] whitespace-nowrap text-right',
    render: (row) => formatAmount(row.net_claim_amount as string | number),
  },
  {
    id: 'rental_status_date',
    label: 'Default Notification Date',
    sortKey: 'rental_status_date',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[11rem]`,
    className: 'min-w-[11rem] whitespace-nowrap',
    render: (row) => display(row.rental_status_date),
  },
  {
    id: 'rental_status_last_date',
    label: 'Status Date',
    sortKey: 'rental_status_last_date',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem] whitespace-nowrap',
    render: (row) => display(row.rental_status_last_date),
  },
  {
    id: 'special_note',
    label: 'Special Note',
    sortKey: 'special_note',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[8rem]`,
    className: 'min-w-[8rem]',
    render: (row) => (row.special_note === 'COVID_19_YES' ? 'Covid_19' : '—'),
  },
  {
    id: 'message_added_by',
    label: 'Message Added By',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem]',
    render: (row) => display(row.message_added_by),
  },
  {
    id: 'message_date',
    label: 'Message Date',
    sortKey: 'message_date',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem] whitespace-nowrap',
    render: (row) => display(row.message_date),
  },
  {
    id: 'message',
    label: 'Message to Property',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} w-[18rem]`,
    className: 'w-[18rem] max-w-[18rem] align-top',
    render: (row) => <LegacyHtmlPreview value={row.message} />,
  },
  {
    id: 'notes_added_by',
    label: 'Notes Added By',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem]',
    render: (row) => display(row.notes_added_by),
  },
  {
    id: 'notes_date',
    label: 'Notes Date',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} min-w-[10rem]`,
    className: 'min-w-[10rem] whitespace-nowrap',
    render: (row) => display(row.notes_date),
  },
  {
    id: 'notes',
    label: 'Liberty Notes',
    headerClassName: `${PENDING_DEFAULTS_HEADER_CLASS} w-[18rem]`,
    className: 'w-[18rem] max-w-[18rem] align-top',
    render: (row) => <LegacyHtmlPreview value={row.notes} />,
  },
]
